import React, {
  createContext,
  useContext,
  useMemo,
  PropsWithChildren,
} from "react";
import { QuestionTypeEnum } from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { QuestionModel } from "app/services/api/types/forms/models/QuestionModel";
import {
  AssessmentSubmissionFormModel,
  useAssessmentFormContext,
} from "../../assessments/providers/AssessmentFormProvider";
import {
  maxValueValidation,
  minValueValidation,
  decimalValidation,
} from "ui/utils/Validators";
import { Control } from "react-hook-form";

export interface AssessmentQuestionContextValue {
  question: QuestionModel;
  id: string;
  name: `${number}.value`;
  otherTextName: `${number}.free_text`;
  filesName: `${number}.files`;
  isRequired: boolean;
  isReadOnly: boolean;
  isSingleFile: boolean;
  type: QuestionTypeEnum;
  inputType: "text" | "numeric";
  options: Array<{ label: string; value: string; hasDescription?: boolean }>;
  // react-hook-form
  control: Control<AssessmentSubmissionFormModel>;
  // validation rules for inputs
  rules?: Record<string, unknown>;
}

const AssessmentQuestionContext = createContext<
  AssessmentQuestionContextValue | undefined
>(undefined);

export function useAssessmentQuestionContext() {
  const ctx = useContext(AssessmentQuestionContext);
  if (!ctx)
    throw new Error(
      "useAssessmentQuestionContext must be used within AssessmentQuestionProvider",
    );
  return ctx;
}

export function AssessmentQuestionProvider({
  question,
  children,
}: PropsWithChildren<{ question: QuestionModel }>) {
  const { control, isSubmitting } = useAssessmentFormContext();

  const value: AssessmentQuestionContextValue = useMemo(() => {
    const options =
      question.options?.map((option) => ({
        label: option.title,
        value: option.title,
        hasDescription: option.requires_description,
      })) ?? [];

    const id = `q-${question.id}`;
    const name = `${question.id!}.value` as const;
    const otherTextName = `${question.id!}.free_text` as const;
    const filesName = `${question.id!}.files` as const;
    const isRequired = !!question.required;
    const inputType: "text" | "numeric" =
      question.type === QuestionTypeEnum.NUMBER ? "numeric" : "text";
    const isSingleFile =
      question.type === QuestionTypeEnum.SINGLE_FILE ||
      question.type === QuestionTypeEnum.SINGLE_IMAGE;
    const rules: Record<string, unknown> = {};
    if (question.type === QuestionTypeEnum.NUMBER) {
      rules.validate = { isNumber: decimalValidation };
      if (question.max_value != null)
        rules.max = maxValueValidation(question.max_value);
      if (question.min_value != null)
        rules.min = minValueValidation(question.min_value);
    }

    return {
      question,
      id,
      name,
      otherTextName,
      filesName,
      isRequired,
      isReadOnly: isSubmitting,
      isSingleFile,
      type: question.type,
      inputType,
      options,
      control,
      rules,
    };
  }, [question, isSubmitting, control]);

  return (
    <AssessmentQuestionContext.Provider value={value}>
      {children}
    </AssessmentQuestionContext.Provider>
  );
}
